/**
 * Date/time display helpers.
 *
 * Backend timestamps are stored as IST wall-clock time in a naive form (no offset).
 * We show those digits VERBATIM — `toDisplayDate` yields a Date whose local wall-clock
 * matches the stored value, so `toLocale*` (without a `timeZone` option) prints the
 * stored digits on any runtime, with no +5:30 shift.
 */
import { toDisplayDate } from '../../utils/timezone.util';

/**
 * Format date as DD/MM/YYYY HH:MM AM/PM
 */
export function formatTimestamp(dateInput: string | Date | null | undefined): string {
  const date = toDisplayDate(dateInput);
  if (!date) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format date as DD/MM/YYYY HH:MM:SS AM/PM (includes seconds)
 */
export function formatDateTimeSeconds(dateInput: string | Date | null | undefined): string {
  const date = toDisplayDate(dateInput);
  if (!date) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

/**
 * Format date as DD/MM/YYYY (date only, no time)
 */
export function formatDate(dateInput: string | Date | null | undefined): string {
  const date = toDisplayDate(dateInput);
  if (!date) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format time as HH:MM AM/PM (time only, no date)
 */
export function formatTime(dateInput: string | Date | null | undefined): string {
  const date = toDisplayDate(dateInput);
  if (!date) return '-';
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}
